// src/utils/exportToCursor.js
// Utility functions for exporting ideas to Cursor workflow format

import { auth } from '../firebase';

// Helper function to detect development environment
const isDevelopmentEnvironment = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  const port = window.location.port;
  const isLocalNetwork = /^(192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(hostname);
  return hostname === 'localhost' || 
         hostname === '127.0.0.1' || 
         isLocalNetwork ||
         port === '3000' ||
         process.env.NODE_ENV === 'development';
};

// Get API base URL
const getApiBaseUrl = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || '';
  const isDevelopment = isDevelopmentEnvironment();
  
  if (API_BASE_URL) {
    return API_BASE_URL;
  }
  
  if (isDevelopment) {
    return 'http://localhost:4000';
  }
  
  // Production: Use Cloud Functions URL (already includes /api)
  return 'https://us-central1-epiphoric-prod.cloudfunctions.net/api';
};

// Get full API endpoint URL (handles /api prefix correctly for dev vs prod)
// Note: All Express routes in functions/index.js use the /api/ prefix (e.g., /api/generate-workflow)
// The Cloud Function is exported as 'api', so baseUrl is ...cloudfunctions.net/api
// This means we need /api/api/ for full URL in production
const getApiEndpoint = (endpoint) => {
  const baseUrl = getApiBaseUrl();
  
  // Normalize endpoint to start with /
  let normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  // IMPORTANT: All Express routes have /api/ prefix (see functions/index.js)
  // - Cloud Function base URL: ...cloudfunctions.net/api (function name)
  // - Express route: /api/generate-workflow
  // - Full URL needed: ...cloudfunctions.net/api/api/generate-workflow
  // 
  // Strategy: Ensure endpoint always includes /api/ prefix, then append to baseUrl
  if (!normalizedEndpoint.startsWith('/api/')) {
    // Add /api prefix if not already present
    normalizedEndpoint = `/api${normalizedEndpoint}`;
  }
  
  // Now append to baseUrl
  return `${baseUrl}${normalizedEndpoint}`;
};

/**
 * Generate AI-enhanced workflow markdown using OpenAI API
 * @param {Object} idea - The idea object
 * @param {Object} tags - Tags object mapping tag IDs to tag names
 * @returns {Promise<string>} Generated markdown content
 */
export const generateAIWorkflow = async (idea, tags = {}) => {
  if (!idea) {
    throw new Error('Idea data is required');
  }
  
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User must be authenticated to generate AI workflow');
  }
  
  // Get ID token for authentication
  let idToken;
  try {
    idToken = await user.getIdToken(true); // Force refresh
  } catch (error) {
    console.error('Error getting ID token:', error);
    throw new Error('Authentication failed. Please sign in again.');
  }
  
  // Prepare idea data for API
  const ideaData = {
    idea: idea.idea || '',
    description: idea.description || '',
    tags: Array.isArray(idea.tags) ? idea.tags.map(tagId => tags[tagId] || tagId).filter(Boolean) : [],
    status: idea.status || 'Drafted',
    rating: idea.rating || 0,
    subtasks: idea.subtasks || [],
    resources: idea.resources || [],
    lessonsLearned: idea.lessonsLearned || '',
    color: idea.color || null
  };
  
  // Express route is /api/generate-workflow (see functions/index.js:925)
  // getApiEndpoint will add /api prefix if not present
  const apiUrl = getApiEndpoint('/generate-workflow');
  
  try {
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({ ideaData })
    });
    
    // Check if response is JSON before parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      // If we got HTML or other non-JSON, it's likely an error page
      const text = await response.text();
      console.error('[generateAIWorkflow] Non-JSON response:', {
        status: response.status,
        statusText: response.statusText,
        contentType,
        preview: text.substring(0, 200)
      });
      
      if (response.status === 404) {
        throw new Error('API endpoint not found. Please check that the server is running and the endpoint is configured.');
      } else if (response.status >= 500) {
        throw new Error('Server error. Please try again later or contact support.');
      } else {
        throw new Error(`Unexpected response from server (${response.status}). Please try again.`);
      }
    }
    
    const data = await response.json();
    
    if (!response.ok) {
      // Handle specific error codes
      if (response.status === 401) {
        throw new Error('Authentication failed. Please sign in again.');
      } else if (response.status === 503 && data.code === 'OPENAI_NOT_CONFIGURED') {
        throw new Error('AI service is not configured. Please contact support.');
      } else if (response.status === 429) {
        // Distinguish between quota exceeded and rate limit
        if (data.code === 'QUOTA_EXCEEDED') {
          const errorMsg = data.details 
            ? `${data.error}\n\n${data.details}`
            : data.error || 'OpenAI API quota exceeded. Please check your account billing.';
          throw new Error(errorMsg);
        } else {
          throw new Error('Rate limit exceeded. Please try again in a moment.');
        }
      } else if (response.status === 500) {
        throw new Error(data.message || 'Failed to generate workflow. Please try again.');
      } else {
        throw new Error(data.error || 'Failed to generate workflow');
      }
    }
    
    return data.markdown || '';
  } catch (error) {
    // Handle JSON parsing errors specifically
    if (error instanceof SyntaxError && error.message.includes('JSON')) {
      throw new Error('Server returned invalid response. Please check that the API endpoint is configured correctly.');
    }
    if (error instanceof TypeError && error.message.includes('fetch')) {
      throw new Error('Network error. Please check your connection and try again.');
    }
    throw error;
  }
};

/**
 * Download markdown file to user's downloads directory
 * @param {string} content - Markdown content to download
 * @param {string} filename - Filename (without extension)
 */
export const downloadMarkdownFile = (content, filename) => {
  if (!content) {
    throw new Error('Content is required for download');
  }
  
  // Sanitize filename for filesystem
  const sanitizedFilename = filename
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
    .substring(0, 100); // Limit length
  
  // Add timestamp
  const timestamp = new Date().toISOString().split('T')[0];
  const finalFilename = `${sanitizedFilename}-workflow-${timestamp}.md`;
  
  // Create Blob
  const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' });
  
  // Create download link
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = finalFilename;
  link.style.display = 'none';
  
  // Trigger download
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  setTimeout(() => {
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, 100);
};

/**
 * Generate capture_issue formatted text (non-AI, template-based)
 * @param {Object} idea - The idea object
 * @param {Object} tags - Tags object
 * @param {string} projectHint - Optional project name hint
 * @returns {string} Formatted markdown
 */
export const generateCapturePrompt = (idea, tags = {}, projectHint = '') => {
  const ideaTitle = idea?.idea || 'Untitled Idea';
  const description = idea?.description || '';
  const ideaTags = Array.isArray(idea?.tags) 
    ? idea.tags.map(tagId => tags[tagId] || tagId).filter(Boolean).join(', ')
    : '';
  const status = idea?.status || 'Drafted';
  const rating = idea?.rating || 0;
  
  const project = projectHint || 'this';
  
  return `# Title
${ideaTitle}

# TLDR
${description ? description.split('\n')[0] : 'No description provided'}

# Current Behavior
Currently, ${project} does not have this in place or it doesn't exist yet.

# Desired Behavior
${ideaTitle}

${description ? `\n# Description\n${description}\n` : ''}
${ideaTags ? `\n# Tags\n${ideaTags}\n` : ''}
Status: ${status}
Rating: ${rating}/5

# Notes
This idea was exported from Epiphoric.

# Priority
${rating >= 4 ? 'high' : rating >= 2 ? 'medium' : 'low'}

# Open Questions
- What are the key requirements?
- What are the constraints or limitations?
- What is the expected timeline or deadline?

# Timestamp
${new Date().toISOString()}`;
};

/**
 * Generate explore formatted text (non-AI, template-based)
 * @param {Object} idea - The idea object
 * @param {Object} tags - Tags object
 * @param {string} projectHint - Optional project name hint
 * @returns {string} Formatted markdown
 */
export const generateExplorePrompt = (idea, tags = {}, projectHint = '') => {
  const ideaTitle = idea?.idea || 'Untitled Idea';
  const description = idea?.description || '';
  const ideaTags = Array.isArray(idea?.tags) 
    ? idea.tags.map(tagId => tags[tagId] || tagId).filter(Boolean).join(', ')
    : '';
  const subtasks = idea?.subtasks || [];
  const resources = idea?.resources || [];
  
  const project = projectHint || 'this project';
  
  let subtasksText = '';
  if (Array.isArray(subtasks) && subtasks.length > 0) {
    subtasksText = '\n# Subtasks/TODOs\n' + subtasks.map((st, idx) => {
      const taskText = st.task || st.text || st;
      const completed = st.completed ? ' (completed)' : '';
      const url = st.url ? ` - ${st.url}` : '';
      return `${idx + 1}. ${taskText}${completed}${url}`;
    }).join('\n');
  }
  
  let resourcesText = '';
  if (Array.isArray(resources) && resources.length > 0) {
    resourcesText = '\n# Resources\n' + resources.map(r => {
      const label = r.label || r.url;
      return `- ${label}: ${r.url}`;
    }).join('\n');
  }
  
  return `# Restated Problem
We need to work on: ${ideaTitle}

# User + JTBD
[Who needs this and what job they need done - fill in based on description]

# Current Behavior
${project} currently does not have this in place.

# Desired Behavior
${ideaTitle} with the following requirements:
${description}

${ideaTags ? `\n# Tags\n${ideaTags}\n` : ''}
${subtasksText}
${resourcesText}

# Success Criteria
- Goal is achieved as described
- Outcome meets expectations
- Implementation is effective

# Constraints
- [Add constraints relevant to this idea - budget, time, resources, etc.]

# Risks / Unknowns
- Complexity may be higher than expected
- [Add other risks as needed]

# Open Questions
- What are the specific requirements?
- What resources or dependencies are needed?
- What is the expected timeline?

# Acceptance Criteria (Draft)
- [ ] Goal is achieved
- [ ] Quality standards are met
- [ ] Documentation or notes are updated (if applicable)

# Timestamp
${new Date().toISOString()}`;
};

/**
 * Detect idea type based on keywords and tags
 * Uses keyword matching on idea title, description, and tags to determine the category.
 * Returns 'generic' if no clear match is found.
 * 
 * @param {Object} idea - The idea object with 'idea' (title) and 'description' properties
 * @param {Object} tags - Tags object mapping tag IDs to tag names (optional)
 * @returns {string} Idea type: 'software', 'business', 'personal', 'event', 'creative', or 'generic'
 * 
 * @example
 * detectIdeaType({ idea: 'Build a mobile app', description: 'iOS app for tracking' }, {}) 
 * // returns 'software'
 */
const detectIdeaType = (idea, tags = {}) => {
  const ideaTitle = (idea?.idea || '').toLowerCase();
  const description = (idea?.description || '').toLowerCase();
  const combinedText = `${ideaTitle} ${description}`;
  
  // Check tags for category hints
  const ideaTags = Array.isArray(idea?.tags) 
    ? idea.tags.map(tagId => (tags[tagId] || tagId).toLowerCase()).join(' ')
    : '';
  
  const allText = `${combinedText} ${ideaTags}`;
  
  // Software keywords
  const softwareKeywords = ['build', 'create app', 'develop', 'software', 'app', 'website', 'web app', 'mobile app', 'api', 'backend', 'frontend', 'code', 'programming', 'coder', 'developer', 'implementation'];
  // Business keywords
  const businessKeywords = ['business', 'startup', 'company', 'enterprise', 'profit', 'revenue', 'market', 'sales', 'marketing', 'strategy', 'finance', 'investor'];
  // Personal keywords
  const personalKeywords = ['learn', 'study', 'practice', 'improve', 'skill', 'personal', 'myself', 'achieve', 'goal', 'habit', 'lifestyle'];
  // Event keywords
  const eventKeywords = ['event', 'wedding', 'party', 'conference', 'meeting', 'celebration', 'gathering', 'festival', 'ceremony'];
  // Creative keywords
  const creativeKeywords = ['art', 'creative', 'design', 'write', 'novel', 'book', 'music', 'film', 'video', 'photography', 'paint', 'draw'];
  
  // Check for matches (return first match found)
  if (softwareKeywords.some(keyword => allText.includes(keyword))) {
    return 'software';
  }
  if (businessKeywords.some(keyword => allText.includes(keyword))) {
    return 'business';
  }
  if (personalKeywords.some(keyword => allText.includes(keyword))) {
    return 'personal';
  }
  if (eventKeywords.some(keyword => allText.includes(keyword))) {
    return 'event';
  }
  if (creativeKeywords.some(keyword => allText.includes(keyword))) {
    return 'creative';
  }
  
  // Default to generic if no clear match
  return 'generic';
};

/**
 * Get adaptive categories based on idea type
 * Returns appropriate task categories for organizing execution plan tasks.
 * Categories vary by domain (software, business, personal, etc.)
 * 
 * @param {string} ideaType - The detected idea type ('software', 'business', 'personal', 'event', 'creative', or 'generic')
 * @returns {string[]} Array of category names for task grouping
 * 
 * @example
 * getAdaptiveCategories('software') 
 * // returns ['Backend', 'Frontend', 'Data', 'Tests', 'Documentation']
 */
const getAdaptiveCategories = (ideaType) => {
  switch (ideaType) {
    case 'software':
      return ['Backend', 'Frontend', 'Data', 'Tests', 'Documentation'];
    case 'business':
      return ['Research', 'Planning', 'Operations', 'Marketing', 'Legal'];
    case 'personal':
      return ['Preparation', 'Execution', 'Follow-up', 'Resources'];
    case 'event':
      return ['Logistics', 'Content', 'Communications', 'Materials', 'Follow-up'];
    case 'creative':
      return ['Concept', 'Production', 'Distribution', 'Resources'];
    default:
      return ['Planning', 'Execution', 'Resources', 'Follow-up'];
  }
};

/**
 * Generate create_plan formatted text (non-AI, template-based)
 * Creates a markdown template for Cursor's /create_plan command following the structure
 * defined in .cursor/commands/create_plan.md. Uses adaptive categories based on idea type.
 * Includes TLDR, Critical Decisions, Issue Context, Execution Plan with categorized tasks,
 * and Acceptance Criteria sections.
 * 
 * @param {Object} idea - The idea object with properties: idea, description, tags, rating, subtasks, resources
 * @param {Object} tags - Tags object mapping tag IDs to tag names (optional, default: {})
 * @param {string} projectHint - Optional project name hint for labeling (default: '')
 * @returns {string} Formatted markdown ready to paste into Cursor
 * 
 * @example
 * const plan = generatePlanPrompt(idea, tags, 'my-project');
 * // Returns markdown with TLDR, Critical Decisions, Execution Plan with adaptive categories, etc.
 */
// eslint-disable-next-line no-unused-vars
export const generatePlanPrompt = (idea, tags = {}, projectHint = '') => {
  const ideaTitle = idea?.idea || 'Untitled Idea';
  const description = idea?.description || '';
  const ideaTags = Array.isArray(idea?.tags) 
    ? idea.tags.map(tagId => tags[tagId] || tagId).filter(Boolean).join(', ')
    : '';
  const rating = idea?.rating || 0;
  const subtasks = idea?.subtasks || [];
  const resources = idea?.resources || [];
  
  const ideaType = detectIdeaType(idea, tags);
  const categories = getAdaptiveCategories(ideaType);
  
  // Generate TLDR from description or title
  const tldr = description ? description.split('\n')[0].substring(0, 150) : `${ideaTitle} - Planning and execution needed`;
  
  // Build execution plan tasks from subtasks or use placeholders
  let executionPlan = '';
  if (Array.isArray(subtasks) && subtasks.length > 0) {
    // Distribute subtasks across categories
    const tasksPerCategory = Math.ceil(subtasks.length / categories.length);
    categories.forEach((category, categoryIdx) => {
      const startIdx = categoryIdx * tasksPerCategory;
      const endIdx = Math.min(startIdx + tasksPerCategory, subtasks.length);
      const categoryTasks = subtasks.slice(startIdx, endIdx);
      
      if (categoryTasks.length > 0) {
        executionPlan += `\n## ${category}\n`;
        categoryTasks.forEach((st) => {
          const taskText = st.task || st.text || st;
          executionPlan += `- [ ] ${taskText}\n`;
        });
      }
    });
  } else {
    // Generic placeholder tasks
    categories.forEach((category) => {
      executionPlan += `\n## ${category}\n`;
      executionPlan += `- [ ] [Add ${category.toLowerCase()} tasks here]\n`;
    });
  }
  
  // Build resources text
  let resourcesText = '';
  if (Array.isArray(resources) && resources.length > 0) {
    resourcesText = '\n# Resources\n' + resources.map(r => {
      const label = r.label || r.url;
      return `- ${label}: ${r.url}`;
    }).join('\n');
  }
  
  return `# TLDR
${tldr}

# Critical Decisions
- Approach should align with goal and constraints
- Consider available resources and timeline
- Prioritize tasks based on dependencies and impact

# Issue Context
This plan is based on the captured issue: ${ideaTitle}

${description ? `\n# Description\n${description}\n` : ''}
${ideaTags ? `\n# Tags\n${ideaTags}\n` : ''}
Priority: ${rating >= 4 ? 'high' : rating >= 2 ? 'medium' : 'low'}
${resourcesText}

# Execution Plan
${executionPlan}

# Acceptance Criteria
- [ ] All planned tasks are completed
- [ ] Goal is achieved as described
- [ ] Quality standards are met
- [ ] Documentation or notes are updated (if applicable)

# Timestamp
${new Date().toISOString()}`;
};

/**
 * Generate execute_plan formatted text (non-AI, template-based)
 * Creates a markdown template for Cursor's /execute_plan command following the structure
 * defined in .cursor/commands/execute_plan.md. Converts idea subtasks into actionable
 * execution steps organized by adaptive categories.
 * 
 * @param {Object} idea - The idea object with properties: idea, description, subtasks
 * @param {Object} tags - Tags object mapping tag IDs to tag names (optional, default: {})
 * @param {string} projectHint - Optional project name hint for labeling (default: '')
 * @returns {string} Formatted markdown ready to paste into Cursor
 * 
 * @example
 * const execute = generateExecutePrompt(idea, tags, 'my-project');
 * // Returns markdown with "What we're building", Non-goals, Execution Steps by category, Validation
 */
// eslint-disable-next-line no-unused-vars
export const generateExecutePrompt = (idea, tags = {}, projectHint = '') => {
  const ideaTitle = idea?.idea || 'Untitled Idea';
  const description = idea?.description || '';
  const subtasks = idea?.subtasks || [];
  const ideaType = detectIdeaType(idea, tags);
  const categories = getAdaptiveCategories(ideaType);
  
  // Generate "What we're building" bullets
  const bullet1 = ideaTitle;
  const bullet2 = description ? description.split('\n')[0].substring(0, 100) : 'Working towards the stated goal';
  const bullet3 = 'Following the plan step by step';
  
  // Build execution steps
  let executionSteps = '';
  if (Array.isArray(subtasks) && subtasks.length > 0) {
    // Convert subtasks to execution steps
    const tasksPerCategory = Math.ceil(subtasks.length / categories.length);
    categories.forEach((category, categoryIdx) => {
      const startIdx = categoryIdx * tasksPerCategory;
      const endIdx = Math.min(startIdx + tasksPerCategory, subtasks.length);
      const categoryTasks = subtasks.slice(startIdx, endIdx);
      
      if (categoryTasks.length > 0) {
        executionSteps += `\n## ${category}\n`;
        categoryTasks.forEach((st) => {
          const taskText = st.task || st.text || st;
          const completed = st.completed ? ' ✅' : '';
          executionSteps += `- [ ] ${taskText}${completed}\n`;
        });
      }
    });
  } else {
    // Generic execution steps
    categories.forEach((category, idx) => {
      executionSteps += `\n## ${category}\n`;
      executionSteps += `- [ ] [Implement ${category.toLowerCase()} tasks]\n`;
      if (idx === 0) {
        executionSteps += `- [ ] [Review and validate ${category.toLowerCase()} work]\n`;
      }
    });
  }
  
  return `# What we're building
- ${bullet1}
- ${bullet2}
- ${bullet3}

# Non-goals
- Scope changes that weren't in the plan
- Refactoring unrelated code
- Introducing new features beyond the plan

# Execution Steps
${executionSteps}

# Validation
- [ ] All steps are completed
- [ ] Work meets quality standards
- [ ] Goal is achieved as planned
- [ ] Any issues or blockers are documented

# Timestamp
${new Date().toISOString()}`;
};

/**
 * Generate combined export (capture + explore) (non-AI, template-based)
 * Combines capture_issue and explore sections into a single markdown export.
 * This is the 2-section export format.
 * 
 * @param {Object} idea - The idea object
 * @param {Object} tags - Tags object mapping tag IDs to tag names (optional, default: {})
 * @param {string} projectHint - Optional project name hint (default: '')
 * @returns {string} Formatted markdown with capture and explore sections separated by ---
 */
export const generateCombinedExport = (idea, tags = {}, projectHint = '') => {
  const capture = generateCapturePrompt(idea, tags, projectHint);
  const explore = generateExplorePrompt(idea, tags, projectHint);
  
  return `${capture}

---

${explore}`;
};

/**
 * Generate complete workflow export (capture + explore + plan + execute) (non-AI, template-based)
 * Combines all four Cursor workflow sections into a single markdown export:
 * 1. capture_issue section
 * 2. explore section  
 * 3. create_plan section
 * 4. execute_plan section
 * 
 * This provides parity with the AI-enhanced download feature but uses template-based generation
 * (faster, no API calls, works offline). Content is simpler than AI-generated but follows
 * the same structure and is immediately usable in Cursor.
 * 
 * @param {Object} idea - The idea object with all standard properties
 * @param {Object} tags - Tags object mapping tag IDs to tag names (optional, default: {})
 * @param {string} projectHint - Optional project name hint (default: '')
 * @returns {string} Formatted markdown with all four sections separated by ---
 * 
 * @example
 * const workflow = generateCompleteWorkflow(idea, tags, 'epiphoric');
 * // Returns complete markdown ready to paste into Cursor with all 4 workflow sections
 */
export const generateCompleteWorkflow = (idea, tags = {}, projectHint = '') => {
  const capture = generateCapturePrompt(idea, tags, projectHint);
  const explore = generateExplorePrompt(idea, tags, projectHint);
  const plan = generatePlanPrompt(idea, tags, projectHint);
  const execute = generateExecutePrompt(idea, tags, projectHint);
  
  return `${capture}

---

${explore}

---

${plan}

---

${execute}`;
};
